import { ButtonPrimary, ButtonSecondary } from "@nova/component-btn"
import { Dropdown } from "@nova/component-dropdown"
import * as Icons from "@nova/component-icons"
import { PilotTrackingTagMissionSpec } from "@nova/schemas"
import { useAppSelector } from "integrations/redux/hooks"
import { useCallback, useRef, useState } from "react"
import { selectSelectedMissionIds } from "../redux/missions-table-selectors"
import { useBatchTagMissions } from "../tags/TagQueries"
import { useDownloadFlightRecords, useExportFlightPaths } from "./PilotTrackingMissionQueries"
import { MissionTagDropdownPanel, MissionTagDropdownPanelRef } from "./MissionTagDropdownPanel"

type Panel = "main" | "tags" | "export"

export const MoreActionsDropdown = ({
    missions,
    onSubmit,
}: {
    missions: { missionId: string; tagIds: string[] }[]
    onSubmit: () => void
}) => {
    const [panel, setPanel] = useState<Panel>("main")

    const selectedMissionIds = useAppSelector(selectSelectedMissionIds)
    const { downloadFlightRecords, isDownloading } = useDownloadFlightRecords()
    const { exportFlightPaths, isExporting } = useExportFlightPaths()
    const { batchTagMissions } = useBatchTagMissions()
    const tagPanelRef = useRef<MissionTagDropdownPanelRef>(null)

    const resetState = useCallback(() => {
        setPanel("main")
    }, [])

    const handleTagCommit = useCallback(
        (additions: PilotTrackingTagMissionSpec[], deletions: PilotTrackingTagMissionSpec[]) => {
            void batchTagMissions({ additions, deletions }).then(() => {
                onSubmit()
            })
        },
        [batchTagMissions, onSubmit]
    )

    const missionCount = Object.keys(selectedMissionIds).length

    return (
        <Dropdown onClose={resetState}>
            <Dropdown.Trigger as="div">
                <ButtonSecondary title="More" RightIcon={Icons.ThreeDotsSettings} />
            </Dropdown.Trigger>
            <Dropdown.Menu
                unstyled
                className="rounded-xl bg-gray-700 shadow-dp4 overflow-hidden w-auto min-w-[240px]"
                position="bottom-right"
            >
                {panel !== "main" && (
                    <Dropdown.Item
                        clickBehaviour="keep-open"
                        onClick={() => setPanel("main")}
                        className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-400 hover:text-white hover:bg-gray-500 transition-colors cursor-pointer"
                    >
                        <Icons.SmallIcon Icon={Icons.ArrowLeft} />
                        Back
                    </Dropdown.Item>
                )}

                {panel === "main" && (
                    <>
                        <Dropdown.Item
                            clickBehaviour="keep-open"
                            onClick={() => setPanel("tags")}
                            className="flex items-center justify-between px-4 py-2.5 text-sm text-white hover:bg-gray-500 transition-colors cursor-pointer"
                        >
                            Add Tags
                            <Icons.SmallIcon Icon={Icons.ArrowRight} className="text-gray-400" />
                        </Dropdown.Item>
                        <Dropdown.Item
                            clickBehaviour="keep-open"
                            onClick={() => setPanel("export")}
                            className="flex items-center justify-between px-4 py-2.5 text-sm text-white hover:bg-gray-500 transition-colors cursor-pointer"
                        >
                            Export Flight{missionCount !== 1 && "s"}
                            <Icons.SmallIcon Icon={Icons.ArrowRight} className="text-gray-400" />
                        </Dropdown.Item>
                    </>
                )}

                {panel === "tags" && (
                    <>
                        <MissionTagDropdownPanel
                            ref={tagPanelRef}
                            missions={missions}
                            onCommit={handleTagCommit}
                        />
                        <div className="flex justify-end px-3 py-2 border-t border-gray-600">
                            <Dropdown.Close
                                as={ButtonPrimary}
                                title="Apply"
                                onClick={() => tagPanelRef.current?.commit()}
                            />
                        </div>
                    </>
                )}

                {panel === "export" && (
                    <>
                        <Dropdown.Item
                            onClick={() => downloadFlightRecords(Object.keys(selectedMissionIds))}
                            clickBehaviour="wait"
                            className="px-4 py-2.5 text-sm text-white hover:bg-gray-500 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isDownloading ? "Downloading..." : "Data File"}
                        </Dropdown.Item>
                        <Dropdown.Item
                            onClick={() => exportFlightPaths(Object.keys(selectedMissionIds))}
                            clickBehaviour="wait"
                            className="px-4 py-2.5 text-sm text-white hover:bg-gray-500 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isExporting ? "Exporting..." : "Flight Path"}
                        </Dropdown.Item>
                    </>
                )}
            </Dropdown.Menu>
        </Dropdown>
    )
}
